import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Check, DollarSign, Edit2, Zap, Settings, ChevronsRight, Cpu, Puzzle, Heart, Star } from 'lucide-react';
import clsx from 'clsx';

import { AgentData_V10, AgentHubPageViewMode, PricingSectionPublicV10, SaasPlanV10 } from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import {
  INNER_CARD_ANIMATE_RESTING,
  INNER_CARD_HOVER,
  INNER_CARD_TRANSITION_HOVER,
} from '../../shared/animationConstants';

// Props for the main section component
interface PricingSectionProps {
  agentData: AgentData_V10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

// --- SUB-COMPONENT FOR CUSTOM INTEGRATION PRICING ---
const CustomIntegrationView: React.FC<{ data: PricingSectionPublicV10; onAction: PricingSectionProps['onAction'] }> = ({ data, onAction }) => {
  const { t } = useTranslation('agent-common');
  const integrationData = data.customIntegrationData;

  if (!integrationData) return null;

  const { feeRange, feeIncludes, interactionCostRange, factorsInfluencingCost, primaryCta } = integrationData;

  const factorsIcons: { [key: string]: React.ReactNode } = {
    complexity: <Zap size={18} className="text-amber-500" />,
    llmChoice: <Cpu size={18} className="text-sky-500" />,
    volume: <ChevronsRight size={18} className="text-indigo-500" />,
    tools: <Puzzle size={18} className="text-rose-500" />,
  };

  const InfoListItem: React.FC<{ textKey: string; tFunc: Function; }> = ({ textKey, tFunc }) => (
    <li className="flex items-start gap-3">
      <Check size={20} className="text-emerald-500 mt-0.5 flex-shrink-0" />
      <span className="text-gray-700">{tFunc(textKey)}</span>
    </li>
  );

  return (
    <div className="space-y-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <motion.div animate={INNER_CARD_ANIMATE_RESTING} whileHover={INNER_CARD_HOVER} transition={INNER_CARD_TRANSITION_HOVER} className="bg-slate-50 border-2 border-black rounded-xl p-6 h-full flex flex-col">
          <div className="mb-4">
            <h3 className="text-xl font-semibold text-gray-800 mb-1">{t('pricingSection.subheading.integrationPackage')}</h3>
            <p className="text-sm text-gray-500">{t('pricingSection.oneTimeFee')}</p>
          </div>
          <div className="text-center bg-white border-2 border-black rounded-lg py-4 mb-5">
            <p className="text-xs text-gray-600 mb-1">{t('pricingSection.feeRangeDescription')}</p>
            <p className="text-3xl font-bold text-gray-900">${feeRange.min.toLocaleString()} - ${feeRange.max.toLocaleString()}</p>
          </div>
          <h4 className="font-semibold text-gray-700 mb-3">{t('pricingSection.feeIncludesTitle')}</h4>
          <ul className="space-y-2.5 flex-grow">
            {feeIncludes.map(itemKey => <InfoListItem key={itemKey} textKey={itemKey} tFunc={t} />)}
          </ul>
        </motion.div>
        <motion.div animate={INNER_CARD_ANIMATE_RESTING} whileHover={INNER_CARD_HOVER} transition={INNER_CARD_TRANSITION_HOVER} className="bg-slate-50 border-2 border-black rounded-xl p-6 h-full flex flex-col">
          <div className="mb-4">
            <h3 className="text-xl font-semibold text-gray-800 mb-1">{t('pricingSection.subheading.ongoingUsage')}</h3>
            <p className="text-sm text-gray-500">{t('pricingSection.ongoingCostDescription')}</p>
          </div>
          <div className="text-center bg-white border-2 border-black rounded-lg py-4 mb-5">
            <p className="text-3xl font-bold text-gray-900">~${interactionCostRange.min.toFixed(4)} - ${interactionCostRange.max.toFixed(3)}</p>
            <p className="text-xs text-gray-600 mt-1">{t('pricingSection.perInteraction')}</p>
          </div>
          <h4 className="font-semibold text-gray-700 mb-3">{t('pricingSection.factorsInfluencingCostTitle')}</h4>
          <ul className="space-y-2.5 flex-grow">
            {factorsInfluencingCost.map(itemKey => {
              const iconKey = itemKey.split('.').pop() || 'complexity';
              return ( <li key={itemKey} className="flex items-start gap-3"> <div className="mt-0.5">{factorsIcons[iconKey]}</div> <span className="text-gray-700">{t(itemKey)}</span> </li> );
            })}
          </ul>
        </motion.div>
      </div>
      <div className="pt-6">
        <motion.div animate={INNER_CARD_ANIMATE_RESTING} whileHover={INNER_CARD_HOVER} transition={INNER_CARD_TRANSITION_HOVER} className="bg-slate-50 border-2 border-black rounded-xl p-6 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2 space-y-4 text-center md:text-left">
              <div className="inline-flex items-center gap-2.5 text-green-800 bg-green-100 rounded-lg p-3 border-2 border-black shadow-sm">
                <Heart size={20} className="flex-shrink-0" />
                <p className="text-sm font-medium">{t('pricingSection.ethicalContributionNote')}</p>
              </div>
              <p className="text-sm text-gray-600 md:max-w-md">{t('pricingSection.freeAuditExplainer')}</p>
            </div>
            <div className="flex justify-center md:justify-end">
              <Button textKey={primaryCta.textKey} variant="primary" size="lg" icon={<Edit2 size={18} />} onClick={() => onAction(primaryCta.actionType, primaryCta.actionPayload)} className="w-full md:w-auto" />
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};


// --- NEWLY IMPLEMENTED SUB-COMPONENT FOR TIERED SAAS PRICING ---
const TieredSaaSView: React.FC<{ data: PricingSectionPublicV10; onAction: PricingSectionProps['onAction'] }> = ({ data, onAction }) => {
  const plans = data.saasPlans || [];

  if (plans.length === 0) {
    return <p className="text-center py-8 text-gray-500">Pricing plans are not available at this time.</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
      {plans.map((plan: SaasPlanV10) => (
        <motion.div
          key={plan.id}
          animate={INNER_CARD_ANIMATE_RESTING}
          whileHover={INNER_CARD_HOVER}
          transition={INNER_CARD_TRANSITION_HOVER}
          className={clsx(
            "bg-white border-2 rounded-xl p-6 flex flex-col text-center relative shadow-md",
            plan.isPopular ? 'border-lime-500' : 'border-black'
          )}
        >
          {plan.isPopular && (
            <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-lime-400 text-black px-3 py-1 rounded-full text-xs font-bold border-2 border-black shadow-sm flex items-center gap-1.5">
              <Star size={14} /> Most Popular
            </div>
          )}
          <h3 className="text-xl font-bold text-gray-900 mt-2">{plan.name}</h3>
          <p className="my-4">
            <span className="text-4xl font-extrabold text-gray-900">${plan.price}</span>
            <span className="text-base font-medium text-gray-500">/{plan.period}</span>
          </p>
          <ul className="space-y-3 text-left mb-6 flex-grow border-t border-gray-100 pt-6">
            {plan.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <Check size={20} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
          <Button
            text="Choose Plan"
            variant={plan.isPopular ? 'primary' : 'secondary'}
            fullWidth
            onClick={() => onAction('CHOOSE_SAAS_PLAN', { planId: plan.id })}
            className="mt-auto"
          />
        </motion.div>
      ))}
    </div>
  );
};


// --- MAIN PRICING SECTION COMPONENT ---
export const PricingSection = React.forwardRef<HTMLDivElement, PricingSectionProps>(
  ({ agentData, viewMode, onAction, headerBgColor, id }, ref) => {
    const { t } = useTranslation('agent-common');
    const isOwnerView = viewMode === 'owner_dashboard';
    const pricingData = agentData.pricingSectionPublic;

    // --- OWNER VIEW ---
    if (isOwnerView) {
      return (
        <Card
          id={id} ref={ref}
          titleKey="pricingSection.ownerTitle"
          icon={<DollarSign size={22} className="text-lime-600" />}
          headerBgColor={headerBgColor}
          actionButton={
            agentData.ownerMonetizationDashboard?.managePricingAndListingUrl && (
              <Button text="Manage Pricing" variant="outline" size="sm" icon={<Settings size={14} />} onClick={() => onAction('MANAGE_PRICING', { url: agentData.ownerMonetizationDashboard?.managePricingAndListingUrl })} />
            )
          }
        >
          <p className="text-center text-gray-400 italic py-8">Owner monetization dashboard view would be displayed here.</p>
        </Card>
      );
    }
    
    // --- PUBLIC VIEW ---
    if (!pricingData) return null;

    const renderContent = () => {
      switch (pricingData.offeringType) {
        case 'CustomIntegration':
          return <CustomIntegrationView data={pricingData} onAction={onAction} />;
        case 'TieredSaaS':
          return <TieredSaaSView data={pricingData} onAction={onAction} />;
        case 'EarlyAdopterSaaS':
          return <p className="text-center py-8">Early Adopter SaaS View Coming Soon</p>;
        default:
          return <p className="text-center text-red-500 py-8">Invalid pricing model configured.</p>;
      }
    };

    return (
      <Card
        id={id} ref={ref}
        titleKey={pricingData.titleKey || 'pricingSection.defaultTitle'}
        icon={<DollarSign size={22} className="text-lime-600" />}
        headerBgColor={headerBgColor}
      >
        {renderContent()}
      </Card>
    );
  }
);

PricingSection.displayName = "PricingSection";
